import {
  AssignedLearnpathLanguages,
  AssignedLearnpathSetLanguageRequest,
  AssignedLearnpathSetLanguageResponse,
  LearnpathTranslations,
  LearnpathTranslationsPutRequest,
  ManagedLearnpathLanguage,
  ManagedLearnpathLanguagePostRequest,
  ManagedLearnpathLanguagePutRequest,
  ManagedLearnpathLanguages,
} from '@/api/generated-api-and-types'
import { ContentLocale } from '@/locale/types/Locale'

import { createApiMethod, type } from './utils/createApiMethod'

export const learnpathsTranslations = {
  managed: {
    languages: {
      list: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/languages',
        response: type<ManagedLearnpathLanguages>(),
      }),

      add: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/languages',
        data: type<ManagedLearnpathLanguagePostRequest>(),
        response: type<ManagedLearnpathLanguage>(),
      }),

      toggle: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/languages/:code',
        data: type<ManagedLearnpathLanguagePutRequest>(),
        response: type<ManagedLearnpathLanguage>(),
      }),

      delete: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/languages/:code',
      }),
    },

    translations: {
      details: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/translations/:code',
        response: type<LearnpathTranslations>(),
      }),

      update: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/translations/:code',
        data: type<LearnpathTranslationsPutRequest>(),
        response: type<LearnpathTranslations>(),
      }),
    },
  },

  assigned: {
    languages: {
      list: createApiMethod({
        method: 'GET',
        path: '/learnpaths/assigned/:learnpathId/languages',
        response: type<AssignedLearnpathLanguages>(),
      }),
    },

    setLanguage: createApiMethod({
      method: 'POST',
      path: '/learnpaths/assigned/:learnpathId/set-language',
      data: type<AssignedLearnpathSetLanguageRequest>(),
      response: type<AssignedLearnpathSetLanguageResponse>(),
    }),
  },
}

export const learnpathsTranslationsQueries = {
  all: () => ['learnpaths-translations'] as const,
  managed: (learnpathId: string) =>
    [...learnpathsTranslationsQueries.all(), 'managed', learnpathId] as const,
  managedLanguages: (learnpathId: string) => ({
    queryKey: [
      ...learnpathsTranslationsQueries.managed(learnpathId),
      'languages',
    ] as const,
    queryFn: () =>
      learnpathsTranslations.managed.languages.list({
        params: { learnpathId },
      }),
  }),
  managedTranslations: (learnpathId: string, code: ContentLocale) => ({
    queryKey: [
      ...learnpathsTranslationsQueries.managed(learnpathId),
      'translations',
      code,
    ] as const,
    queryFn: () =>
      learnpathsTranslations.managed.translations.details({
        params: { learnpathId, code },
      }),
  }),
  assignedLanguages: (learnpathId: string) => ({
    queryKey: [
      ...learnpathsTranslationsQueries.all(),
      'assigned',
      learnpathId,
      'languages',
    ] as const,
    queryFn: () =>
      learnpathsTranslations.assigned.languages.list({
        params: { learnpathId },
      }),
  }),
}
